import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { BetManager } from "../Managers/BetManager";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbols } from "../GameComponents/Symbols";
import { Paylines } from "../GameComponents/Paylines";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Payline } from "../GameComponents/Payline";
import { BaseView } from "../GameComponents/Base/BaseView";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../Managers/ModuleManager";
import { Tweens } from "../Components/Effects/Tweens";
import { PlayList } from "../Components/Sounds/PlayList";
import { GameManager } from "../Managers/GameManager";

var WinType = Communication.Game.WinType;

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    private _isFreespins: boolean = false;
    private _showWinValue: boolean = false;

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        isFreespins?: boolean,
    ): void {
        if (isFreespins) {
            this._isFreespins = true;
        }
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);
    }

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        let isOnlyScatter = false;
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                    isOnlyScatter = true;
                }
            }
        }
        this._showWinValue = !isOnlyScatter;
        super.showInitWins(wins, win);
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this._showWinValue = true;
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.playSoundBasedOnPayout();
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 3;
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNormal, true);
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showFirstWins(): void {
        if (ModuleManager.freespinsEnabled()) {
            this.allSymbolsGoToCase(Symbol.CaseNormal, true);
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        }
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
        }

        this.currentCycle = 0;
        this.cycleWins();
        this.visible = true;
        this.startAnimation();
    }

    protected finishFirstWins(): void {
        let isOnlyScatter = false;
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                    isOnlyScatter = true;
                }
            }
        }
        this._showWinValue = !isOnlyScatter;
        this.emit(WinAnimator.FinishWinAnimator);
    }

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        if (BaseView.ShakeReelConf == null) {
            return;
        }
        const symbolIndex = this.currentReels[reelIndex].symbols.indexOf(11);

        if (symbolIndex !== -1) {
            for (let i: number = 0; i < this.currentSymbols[reelIndex].length; i++) {
                let canTease: boolean = true;
                if (reelIndex === 2 && this.countScatters(2) < 4) {
                    canTease = false;
                } else if (reelIndex === 3 && this.countScatters(3) < 7) {
                    canTease = false;
                }
                if (canTease) {
                    Tweens.removeTweens(this.currentSymbols[reelIndex][i]);
                    Tweens.positionTween(this.currentSymbols[reelIndex][i], {
                        tweenX: [
                            this.currentSymbols[reelIndex][i].x,
                            this.currentSymbols[reelIndex][i].x,
                            this.currentSymbols[reelIndex][i].x,
                            this.currentSymbols[reelIndex][i].x,
                        ],
                        tweenY: [
                            this.currentSymbols[reelIndex][i].y,
                            this.currentSymbols[reelIndex][i].y + BaseView.ShakeReelConf[0],
                            this.currentSymbols[reelIndex][i].y + BaseView.ShakeReelConf[1],
                            this.currentSymbols[reelIndex][i].y,
                        ],
                        tweenDuration: 10,
                    });

                    if (reelIndex === 1) {
                        this.emit("scatterIndex1");
                    } else if (reelIndex === 2) {
                        this.emit("scatterIndex2");
                    } else if (reelIndex === 3) {
                        this.emit("scatterIndex3");
                    }
                }
            }
        }
    }

    private countScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (GameManager.baseResponse.screen.reels[i].symbols.indexOf(11) !== -1) {
                for (let j = 0; j < GameManager.baseResponse.screen.reels[i].symbols.length; j++) {
                    if (GameManager.baseResponse.screen.reels[i].symbols[j] === 11) {
                        count++;
                    }
                }
            }
        }

        return count;
    }

    public softReset(): void {
        this.allSymbolsGoToCase(Symbol.CaseNormal, true);
    }

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        PlayList.stop("SND_Symbol11");
        this.stopTimer();

        this.emptyWinFrames();

        this.allSymbolsGoToCase(showLastSymbols ? Symbol.CaseNormal : Symbol.CaseEmpty, false);
        this.allPaylinesGoToCase(Payline.CaseNormal);

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];
                Tweens.removeTweens(symbol);
                symbol.position.x = this.reelX[i] - symbol.offsetX;
                symbol.position.y = this.reelY[i] + this.symbolHeight * j - symbol.offsetY;
                this.noWinSymbolsContainer.addChild(symbol);
            }
        }

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;

                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;

                return lastMultiplierIndex >= 3;
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;

            return lastMultiplierIndex >= 3;
        }
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }
    public getAnimationIndex(): number {
        if (
            this.currentWins.length === 1 &&
            this.currentWins[0].trigger === Communication.Game.WinTrigger.Scatter &&
            this.currentWin === 0
        ) {
            return 6;
        } else {
            return super.getAnimationIndex();
        }
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            const id = this.currentReels[reelIndex].symbols[symbolIndex];

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (id === 11) {
                this.symbolsContainer.addChild(symbol);
            } else {
                this.noWinSymbolsContainer.addChild(symbol);
            }

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                this.paylines.goToCase(caseType, win.paylineId);

                this.emptyWinFrames();
                if (caseType === "WIN") {
                    this.tintFrames(win.paylineId);
                }

                const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                    win.paylineId,
                    this.paylines.paylineDefinition,
                );

                for (let i = 0; i < definition.length; i++) {
                    if (win.mask[i] === 1) {
                        const symbol = this.currentSymbols[i][definition[i]];

                        if (this._isFreespins) {
                            switch (i) {
                                case 0:
                                    this.positionWinFrame(
                                        FreespinsView.WinFrame0,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 1:
                                    this.positionWinFrame(
                                        FreespinsView.WinFrame1,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 2:
                                    this.positionWinFrame(
                                        FreespinsView.WinFrame2,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 3:
                                    this.positionWinFrame(
                                        FreespinsView.WinFrame3,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 4:
                                    this.positionWinFrame(
                                        FreespinsView.WinFrame4,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                            }
                        } else {
                            switch (i) {
                                case 0:
                                    this.positionWinFrame(
                                        BaseView.WinFrame0,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 1:
                                    this.positionWinFrame(
                                        BaseView.WinFrame1,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 2:
                                    this.positionWinFrame(
                                        BaseView.WinFrame2,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 3:
                                    this.positionWinFrame(
                                        BaseView.WinFrame3,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                                case 4:
                                    this.positionWinFrame(
                                        BaseView.WinFrame4,
                                        this.reelX[i],
                                        this.reelY[i] + definition[i] * this.symbolHeight,
                                    );
                                    break;
                            }
                        }
                    }
                }
            }
        }
    }

    private positionWinFrame(winframe: SwitchElement, x: number, y: number): void {
        if (winframe != null) {
            winframe.x = x;
            winframe.y = y;
            winframe.goToCase("SHOW");
        }
    }

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        const win = this.currentWins[this.currentCycle];

        if (win.type == WinType.freeSpins) {
            this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;
        }

        if (this.currentWins == null) {
            return;
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        if (!ModuleManager.freespinsEnabled()) {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];
                    this.noWinSymbolsContainer.addChild(symbol);
                }
            }
        }

        if (this._showWinValue && this.currentWins.length > 1) {
            PlayList.play("SND_BaseWinline");
        }

        this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, this._showWinValue);
        this.winPaylineGoToCase(this.currentCycle, Payline.CaseWin);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    protected getCycleWinsDelay(): number {
        return 60;
    }

    public tintFrames(paylineId: number): void {
        switch (paylineId) {
            case 1:
                ImageElement.DynamicTint = "00ff00";
                break;
            case 2:
                ImageElement.DynamicTint = "0054ff";
                break;
            case 3:
                ImageElement.DynamicTint = "ecab00";
                break;
            case 4:
                ImageElement.DynamicTint = "ff0000";
                break;
            case 5:
                ImageElement.DynamicTint = "009cff";
                break;
            case 6:
                ImageElement.DynamicTint = "ff8a00";
                break;
            case 7:
                ImageElement.DynamicTint = "00d39a";
                break;
            case 8:
                ImageElement.DynamicTint = "ff0084";
                break;
            case 9:
                ImageElement.DynamicTint = "ff8dc8";
                break;
            case 10:
                ImageElement.DynamicTint = "9c00ff";
                break;
            case 11:
                ImageElement.DynamicTint = "ff5c5c";
                break;
            case 12:
                ImageElement.DynamicTint = "bd91ff";
                break;
            case 13:
                ImageElement.DynamicTint = "ffb900";
                break;
            case 14:
                ImageElement.DynamicTint = "008684";
                break;
            case 15:
                ImageElement.DynamicTint = "a7ba00";
                break;
            case 16:
                ImageElement.DynamicTint = "ff7e00";
                break;
            case 17:
                ImageElement.DynamicTint = "a80000";
                break;
            case 18:
                ImageElement.DynamicTint = "d256dd";
                break;
            case 19:
                ImageElement.DynamicTint = "8400d8";
                break;
            case 20:
                ImageElement.DynamicTint = "0b7500";
                break;
            case 21:
                ImageElement.DynamicTint = "6333ff";
                break;
            case 22:
                ImageElement.DynamicTint = "4bb793";
                break;
            case 23:
                ImageElement.DynamicTint = "ff7a7a";
                break;
            case 24:
                ImageElement.DynamicTint = "b99e31";
                break;
            case 25:
                ImageElement.DynamicTint = "915e06";
                break;
            case 26:
                ImageElement.DynamicTint = "1a73a1";
                break;
            case 27:
                ImageElement.DynamicTint = "9e0067";
                break;
            case 28:
                ImageElement.DynamicTint = "38639c";
                break;
            case 29:
                ImageElement.DynamicTint = "79a121";
                break;
            case 30:
                ImageElement.DynamicTint = "13a000";
                break;
        }
    }

    public emptyWinFrames(): void {
        if (BaseView.WinFrame0 != null) BaseView.WinFrame0.goToCase("EMPTY");
        if (BaseView.WinFrame1 != null) BaseView.WinFrame1.goToCase("EMPTY");
        if (BaseView.WinFrame2 != null) BaseView.WinFrame2.goToCase("EMPTY");
        if (BaseView.WinFrame3 != null) BaseView.WinFrame3.goToCase("EMPTY");
        if (BaseView.WinFrame4 != null) BaseView.WinFrame4.goToCase("EMPTY");
        if (FreespinsView.WinFrame0 != null) FreespinsView.WinFrame0.goToCase("EMPTY");
        if (FreespinsView.WinFrame1 != null) FreespinsView.WinFrame1.goToCase("EMPTY");
        if (FreespinsView.WinFrame2 != null) FreespinsView.WinFrame2.goToCase("EMPTY");
        if (FreespinsView.WinFrame3 != null) FreespinsView.WinFrame3.goToCase("EMPTY");
        if (FreespinsView.WinFrame4 != null) FreespinsView.WinFrame4.goToCase("EMPTY");
    }

    private playSoundBasedOnPayout(payoutId: number = -1): void {
        let id: number;

        if (payoutId !== -1) {
            id = payoutId;
        } else {
            let highestWin: Communication.Game.IWin = null;

            for (let i: number = 0; i < this.currentWins.length; i++) {
                if (highestWin == null) {
                    highestWin = this.currentWins[i];
                } else {
                    if (highestWin.amount > this.currentWins[i].amount) {
                        highestWin = this.currentWins[i];
                    }
                }
            }

            if (highestWin != null && highestWin.payoutId != null) {
                id = highestWin.payoutId;
            }
        }

        if (id != null) {
            switch (id) {
                case 1:
                    break;
                case 2:
                    break;
                case 3:
                    break;
                case 4:
                    break;
                case 5:
                    PlayList.play("SND_Symbol5");
                    break;
                case 6:
                    PlayList.play("SND_Symbol6");
                    break;
                case 7:
                    PlayList.play("SND_Symbol7");
                    break;
                case 8:
                    PlayList.play("SND_Symbol8");
                    break;
                case 9:
                    PlayList.play("SND_Symbol9");
                    break;
                case 10:
                    PlayList.play("SND_Symbol10");
                    break;
                case 11:
                    PlayList.loop("SND_Symbol11");
                    break;
                case 12:
                    PlayList.loop("SND_Symbol11");
                    break;
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/